"use client";

import { useMemo, useCallback, useEffect, useRef } from "react";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { logout } from "@/app/_module/services/auth";
import supabaseAuthService from "@/app/_module/services/supabaseAuthService";
import { safeReload } from "@/app/_module/utils/safeReload";

interface TrialStatus {
  isTrialActive: boolean;
  trialEndDate: string | null;
  isExpired: boolean;
  daysRemaining: number;
  isViewerMode: boolean;
  isLoading: boolean;
  error: string | null;
  subscriptionStatus?: string;
  accountStatus?: string;
  hasActiveSubscription: boolean;
  hasPaidSubscription: boolean; // NEW: specifically for paid plans
  trialStartDate?: string;
  trialType?: 'basic' | 'full_fledged';
}

// No need for sessionStorage flags - just logout once if trial expired, like session expiration

/**
 * useTrialStatus - Computes trial/subscription status from user context
 */
export function useTrialStatus() {
  const { user, isInitializing, refetchUser } = useUserContext();
  const hasLoggedOutRef = useRef(false);

  // Compute all trial status values from user context - NO API CALL
  const trialStatus = useMemo<TrialStatus>(() => {
    // Still initializing user context
    if (isInitializing || !user?.user) {
      return {
        isTrialActive: false,
        trialEndDate: null,
        isExpired: false,
        daysRemaining: 0,
        isViewerMode: false,
        isLoading: isInitializing,
        error: null,
        hasActiveSubscription: false,
        hasPaidSubscription: false,
      };
    }

    const userData = user.user;

    // Enterprise users are fully covered - no trial, no billing, no lockdown
    const isEnterpriseAgency = (userData as any).isEnterpriseAgency === true;
    const isEnterprise = userData.accountType === 'enterprise' || isEnterpriseAgency;
    if (isEnterprise) {
      return {
        isTrialActive: false,
        trialEndDate: null,
        isExpired: false,
        daysRemaining: 0,
        isViewerMode: false,
        isLoading: false,
        error: null,
        hasActiveSubscription: true,
        hasPaidSubscription: true,
      };
    }

    const now = new Date();

    // Parse trial dates
    const trialEndDate = userData.trialExpiresAt 
      ? (typeof userData.trialExpiresAt === 'string' ? userData.trialExpiresAt : new Date(userData.trialExpiresAt).toISOString())
      : null;
    const trialEndDateObj = trialEndDate ? new Date(trialEndDate) : null;
    
    // Calculate trial expiration
    const isExpired = trialEndDateObj ? now > trialEndDateObj : false;
    
    // Calculate days remaining
    let daysRemaining = 0;
    if (trialEndDateObj && !isExpired) {
      const diffTime = trialEndDateObj.getTime() - now.getTime();
      daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    
    // Determine if trial is active (from database flag AND not expired by time)
    const isTrialActive = userData.isTrialActive === true && !isExpired;
    
    // Check subscription status for inactive/canceled subscriptions
    const subscriptionStatus = (userData as any).subscriptionStatus || '';
    
    // Check if subscription period has ended (for cancelAtPeriodEnd subscriptions)
    // Get subscription period end from user data (could be currentPeriodEnd, nextBillingAt, or subEnd)
    const subscriptionPeriodEnd = (userData as any).currentPeriodEnd || 
                                  (userData as any).nextBillingAt || 
                                  (userData as any).subEnd;
    const subscriptionPeriodEndDate = subscriptionPeriodEnd ? new Date(subscriptionPeriodEnd) : null;
    const isSubscriptionPeriodExpired = subscriptionPeriodEndDate ? now > subscriptionPeriodEndDate : false;
    
    // Check if subscription is canceled but period hasn't ended yet (cancelAtPeriodEnd = true)
    // In this case, subscription status might still be 'active' but will become 'canceled' at period end
    const cancelAtPeriodEnd = (userData as any).cancelAtPeriodEnd || false;
    const isScheduledForCancellation = cancelAtPeriodEnd && subscriptionStatus !== 'canceled';
    
    const isSubscriptionInactive = 
      subscriptionStatus === 'canceled' || 
      subscriptionStatus === 'past_due' || 
      subscriptionStatus === 'unpaid' || 
      subscriptionStatus === 'expired' ||
      subscriptionStatus === 'incomplete' ||
      subscriptionStatus === 'incomplete_expired' ||
      (isScheduledForCancellation && isSubscriptionPeriodExpired); // Period ended and was scheduled for cancellation
    
    // Check account status for suspended/inactive accounts
    const isAccountInactive = 
      userData.accountStatus === 'suspended' || 
      userData.accountStatus === 'inactive';
    
    // NEW: hasPaidSubscription - user has a PAID subscription (not trial) that is ACTIVE
    // IMPORTANT: Even if selectedPlan exists, subscription must be ACTIVE to count as paid
    // This handles cases where subscription is canceled/chargebacked but selectedPlan still exists
    // Also handles cancelAtPeriodEnd: subscription is active until period ends
    const hasPaidSubscription = 
      (userData.accountStatus === 'active' && !isSubscriptionInactive && !isAccountInactive && !isSubscriptionPeriodExpired) ||
      (!!userData.selectedPlan && subscriptionStatus === 'active' && !isAccountInactive && !isSubscriptionPeriodExpired) ||
      (subscriptionStatus === 'active' && !isAccountInactive && !isSubscriptionPeriodExpired);
    
    // hasActiveSubscription - user has ANY active subscription OR active trial
    // This is used for feature access
    const hasActiveSubscription = 
      hasPaidSubscription ||
      (subscriptionStatus === 'trialing' && !isAccountInactive && !isSubscriptionPeriodExpired) ||
      isTrialActive;
    
    // Check if subscription is expired/inactive (similar to trial expiration)
    // This happens when:
    // 1. Subscription status is canceled, past_due, unpaid, expired, incomplete
    // 2. Subscription period has ended (for cancelAtPeriodEnd subscriptions)
    // 3. Account is suspended/inactive
    const isSubscriptionExpired = isSubscriptionInactive || isAccountInactive || (isScheduledForCancellation && isSubscriptionPeriodExpired);
    
    // There are TWO independent ways a user ends up read-only, and until now this
    // hook only knew about one of them:
    //
    //   1. Subscription/trial state on the USER (handled below) — drives
    //      ViewerModeGuard and the lockdown screen.
    //   2. agency_users.role = 'viewer', written by the billing freeze
    //      (convertUserToViewer). This is what the BACKEND enforces on every
    //      write — "Viewers can only perform read operations".
    //
    // (2) never reached this hook, so a user frozen for non-payment got their
    // writes rejected and their nav greyed out with NO explanation anywhere:
    // no banner, no lockdown, just buttons that fail. Treating the agency role
    // as viewer mode lines the UI up with what the server actually enforces.
    const isAgencyRoleViewer =
      String((user as any)?.currentAgency?.role || '').toLowerCase() === 'viewer';

    // isViewerMode: True if (trial expired OR subscription inactive OR frozen to
    // the viewer role) AND no paid subscription AND not admin.
    // This treats inactive subscriptions (canceled, chargebacks, payment failures) the same as expired trials
    const isViewerMode =
      (userData as any).isInViewerMode === true ||
      (isAgencyRoleViewer && userData.userType !== 'admin') ||
      ((isExpired || isSubscriptionExpired) && !hasPaidSubscription && userData.userType !== 'admin');
    
    return {
      isTrialActive,
      trialEndDate,
      isExpired,
      daysRemaining,
      isViewerMode,
      isLoading: false,
      error: null,
      subscriptionStatus: (userData as any).subscriptionStatus || userData.accountStatus,
      accountStatus: userData.accountStatus,
      hasActiveSubscription,
      hasPaidSubscription,
      trialStartDate: userData.trialStartDate ? 
        (typeof userData.trialStartDate === 'string' ? userData.trialStartDate : new Date(userData.trialStartDate).toISOString())
        : undefined,
      trialType: (
        userData.trialAccountTypeUsed || 
        (userData as any).trialType || 
        (userData as any).trial_account_type_used ||
        'basic'
      ) as 'basic' | 'full_fledged',
    };
  }, [user?.user, isInitializing]);

  // Trial expiration check - refresh page once when trial expires (not logout)
  // This updates the UI to show expired trial state but keeps user logged in
  useEffect(() => {
    // Skip if user context is still loading or no user
    if (isInitializing || !user?.user?.id) {
      return;
    }

    // Skip if already refreshed (prevents loops)
    if (hasLoggedOutRef.current) {
      return;
    }

    // Skip if on sign-in page
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/auth/sign-in')) {
      return;
    }

    // Skip if no trial or user has paid subscription
    if (!trialStatus.trialEndDate || trialStatus.hasPaidSubscription) {
      return;
    }

    const trialEndTime = new Date(trialStatus.trialEndDate).getTime();
    const now = Date.now();
    const timeUntilExpiry = trialEndTime - now;

    // Only refresh if trial expires in the future (while user is logged in)
    // If trial is already expired, do nothing - user already sees expired state
    if (timeUntilExpiry > 0) {
      // Trial hasn't expired yet - set timer to refresh page when it does
      const timer = setTimeout(() => {
        if (hasLoggedOutRef.current) return;
        
        hasLoggedOutRef.current = true; // Prevent multiple refreshes

        // Just refresh the page - this will reload user context and show expired trial state
        // User stays logged in, just sees the updated trial status.
        //
        // safeReload, NOT location.reload: this fires from a TIMER, so without the
        // guard it can hit mid-dial — killing the live call and popping a native
        // "Leave site?" dialog while the agent is talking to a lead. Deferred until
        // the call ends; the expired-trial state shows a moment later instead.
        safeReload('trial-expired');
      }, timeUntilExpiry);

      return () => clearTimeout(timer);
    }
    // If trial is already expired, do nothing - user already sees expired state
  }, [trialStatus.trialEndDate, trialStatus.hasPaidSubscription, user?.user?.id, isInitializing]);

  // Refresh by re-fetching user context (not a separate API call)
  const refreshTrialStatus = useCallback(() => {
    if (refetchUser) {
      refetchUser((prev) => prev);
    }
  }, [refetchUser]);

  return {
    ...trialStatus,
    refreshTrialStatus,
  };
}

// Hook to check if a feature should be restricted in viewer mode
export function useFeatureAccess() {
  const { isViewerMode, hasActiveSubscription } = useTrialStatus();
  const { user } = useUserContext();

  const canAccessFeature = (feature: string): boolean => {
    // If not in viewer mode or has active subscription, allow all features
    if (!isViewerMode || hasActiveSubscription) return true;

    // Check if user is admin - admins should have access to all features
    const isAdmin = user?.user?.userType === 'admin';
    if (isAdmin) return true;

    // Check if user has any agency role (owner, admin, agent, viewer)
    const hasAgencyRole = user?.currentAgency?.role && ['owner', 'admin', 'agent', 'viewer'].includes(user.currentAgency.role);
    if (hasAgencyRole) return true;

    // Check if user is an independent agent (no agency)
    const isIndependentAgent = user?.user?.userType === 'agent' && !user?.currentAgency;
    if (isIndependentAgent) return true;

    // Define which features are allowed in viewer mode for regular users
    const viewerModeFeatures = [
      'profile',
      'settings',
      'subscription',
      'account',
      'logout',
    ];

    return viewerModeFeatures.includes(feature);
  };

  const getRestrictedMessage = (feature: string): string => {
    return "This feature is not available in viewer mode. Please upgrade your subscription to continue.";
  };

  return {
    canAccessFeature,
    getRestrictedMessage,
    isViewerMode,
  };
}
